import React, { useEffect } from 'react'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { Route, Switch, Redirect, useHistory } from 'react-router-dom'
import classNames from 'classnames'
import moment from 'moment'
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles'

import { prepareLanguage } from 'cityads-v3/i18n/utils'
import { requireMomentLocale } from 'cityads-v3/utils/dateUtils'
import { removeNode } from 'cityads-v3/utils/htmlUtils'
import { useThemeChanger, useBrowserThemeListener } from 'cityads-v3/hooks/theme'

import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

import AuthChecker from 'cityads-v3/components/Auth/AuthChecker'
import AuthReferralSetter from 'cityads-v3/components/Auth/AuthReferralSetter'
import NavigationCounter from 'cityads-v3/components/Counters/NavigationCounter'
import GTMUserIdentifier from 'cityads-v3/components/Counters/GTMUserIdentifier'
import TemporaryRedirect from 'cityads-v3/components/Router/TemporaryRedirect'
import SupportWidget from 'cityads-v3/components/SupportWidget/SupportWidget'
import LanguageListener from 'cityads-v3/components/LanguageListener'
import Balaclava from 'cityads-v3/components/Balaclava'
import CookiePolicy from 'cityads-v3/components/CookiePolicy/CookiePolicy'
import Menu from 'cityads-v3/components/Menu/Menu'
import IsBannedUser from 'cityads-v3/components/IsBannedUser/IsBannedUser'

import NotFound from 'cityads-v3/containers/misc/NotFound'
import WelcomeForm from 'cityads-v3/containers/welcomeform/WelcomeForm'
import AppFabContainer from './AppFabContainer'
import AppReduce from './AppReduce'
import OldUrls from './OldUrls'

import { getList as getCurrencies } from 'cityads-v3/actions/currency'
import { loadUserBalance } from 'cityads-v3/actions/auth'
import { onLoadGetShowMenuSetting } from 'cityads-v3/actions/menu'

import classes from './App.scss'
import { RootState } from 'cityads-v3/types/reducer'

import {
  MainContainer,
  ManagerContainer,
  AboutContainer,
  NewsContainer,
  AdvTypeTrafficContainer,
  WebmasterContainer,
  HeaderComponent,
  FooterComponent,
} from './AppContent.chunks'


const DEFAULT_HEADER_PARAMS: any = {
  showHeader: true,
  showFooter: true,

  backlink: null,
  backlinkText: null,

  title: null,

  tabValue: false,
  tabs: [],

  menuShow: false,
  menuValue: false,
  menu: [],

  showFab: true,
}

export default function AppContent(): JSX.Element {
  useBrowserThemeListener()
  const themeChanger = useThemeChanger()
  const dispatch = useDispatch()
  const history = useHistory()

  const language = useSelector((state: RootState) => state.language.language)
  const menuDocked = useSelector((state: RootState) => state.menu.docked)
  const isMobile = useSelector((state: RootState) => state.app.isMobile)
  const isLoggedIn = useSelector((state: RootState) => state.auth.isLoggedIn)
  const creditsUrl = useSelector((state: RootState) => state.auth.creditsUrl)
  const isLoadedUserBalance = useSelector((state: RootState) => state.auth.isLoadedUserBalance)
  const redirectUrl = useSelector((state: RootState) => state.auth.redirect_url)
  const needWelcomeForm = useSelector((state: RootState) => state.welcomeform.need_welcome_form)
  const leftMenuIsVisible = useSelector((state: RootState) => state.menu.isVisible)

  useEffect(() => {
    removeNode(document.getElementById('jss-server-side'))
    dispatch(getCurrencies())
    dispatch(onLoadGetShowMenuSetting())
  }, [dispatch])

  useEffect(() => {
    if (!isLoadedUserBalance && creditsUrl) {
      dispatch(loadUserBalance(creditsUrl))
    }
  }, [dispatch, isLoadedUserBalance, creditsUrl])

  useEffect(() => {
    const lang = prepareLanguage(language)
    requireMomentLocale(lang)
    moment.locale(lang)
  }, [language])

  useEffect(() => {
    if (redirectUrl) {
      history.push(redirectUrl)
    }
  }, [redirectUrl, history])

  return (
    <ThemeProvider theme={themeChanger.theme}>
      <LocalizationProvider
        dateAdapter={AdapterMoment}
        adapterLocale={language}
      >
        <CssBaseline />
        <AppReduce />
        <AuthChecker />
        <AuthReferralSetter />
        <NavigationCounter />
        <GTMUserIdentifier />
        <SupportWidget />
        <LanguageListener />
        {/* <Agreement /> */}

        <Helmet
          defaultTitle="CityAds Media"
          titleTemplate="%s | CityAds Media"
          htmlAttributes={{
            lang: language,
            'data-theme': themeChanger.themeName
          }}
        />
        <Balaclava {...DEFAULT_HEADER_PARAMS} defaults={DEFAULT_HEADER_PARAMS} />

        <div className="container">
          <Menu />

          <div className={classNames({
            [classes.appContent]: true,
            [classes.appContentMenuHidden]: !leftMenuIsVisible,
            'ca-left-menu-docked': menuDocked && !isMobile,
            [classes.isMobile]: isMobile || (!isLoggedIn && !isMobile),
          })}>
            <HeaderComponent className={classes.header} />

            <div id="content" className={classes.contentArea}>
              <OldUrls />

              <Switch>

                <Route exact path="/" render={() => (
                  <Redirect to="/main" />
                )} />
                <Route exact strict path="/main/" render={() => (
                  <Redirect to="/main" />
                )} />

                <Route path="/main/manager" component={ManagerContainer} />

                <Route path="/main/welcome" component={WelcomeForm} />
                {isLoggedIn && needWelcomeForm && (
                  <TemporaryRedirect to="/main/welcome" />
                )}

                <Route path="/main/about" component={AboutContainer} />
                <Route path="/main/news/:type?" component={NewsContainer} />
                <Route path="/main/advTypeTraffic/:formId" component={AdvTypeTrafficContainer} />
                <Route path="/main" component={MainContainer} />

                <Route path="/publisher" component={WebmasterContainer} />


                <Route component={NotFound} />

              </Switch>
            </div>

            <FooterComponent className={classes.footer} />
          </div>
        </div>

        <AppFabContainer />
        <CookiePolicy />
        <IsBannedUser />
      </LocalizationProvider>
    </ThemeProvider>
  )
}
